import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'

// Help text that folds up on phones. Put it inside any paragraph or
// description (<p>, CardDescription, DialogDescription):
//
//   <CardDescription><HelpText>Long explanation…</HelpText></CardDescription>
//
// On screens narrower than 640px, text longer than four lines shows its first
// two lines and a "More" link; tapping it shows the rest. Shorter text and
// wider screens show everything, with no link.

const PHONE = '(max-width: 639px)'
const MAX_LINES = 4

function usePhone(): boolean {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(PHONE).matches)
  useEffect(() => {
    const query = window.matchMedia(PHONE)
    const update = () => setPhone(query.matches)
    update()
    query.addEventListener('change', update)
    return () => query.removeEventListener('change', update)
  }, [])
  return phone
}

export function HelpText({ children }: { children: ReactNode }) {
  const phone = usePhone()
  const ref = useRef<HTMLSpanElement>(null)
  const [long, setLong] = useState(false)
  const [open, setOpen] = useState(false)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el || !phone) {
      setLong(false)
      return
    }
    // scrollHeight is the full text height even while it's folded.
    const measure = () => {
      const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || 20
      setLong(el.scrollHeight > lineHeight * MAX_LINES + 2)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [phone])

  const folded = phone && long && !open
  return (
    <>
      <span ref={ref} className={folded ? 'line-clamp-2' : 'block'}>
        {children}
      </span>
      {phone && long && (
        <button
          type="button"
          data-help-toggle=""
          className="mt-0.5 block text-xs font-medium text-primary underline underline-offset-2"
          aria-expanded={open}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            setOpen(o => !o)
          }}
        >
          {open ? 'Less' : 'More'}
        </button>
      )}
    </>
  )
}
